import {css, cx} from "@panda/css";

import {PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";

import {NearestPoiTypeStats} from "../../offer/detail/actions/fetch_offer_detail_poi";
import {Country} from "../../region/types/Country";
import {POI_DISTANCE_DEFAULT_VALUE} from "../constants/pois_defaults";
import {IPoi} from "../types/IPoi";
import {IPublicTransportRoute} from "../types/IPublicTransportRoute";
import {PoiType, TransportPoiType} from "../types/PoiType";
import {getHasCountryPois} from "../utils/get_has_country_pois";
import {getNearestPoiDistance} from "../utils/get_nearest_poi_distance";
import {getNearestStopLabel, getPoiPluralizeLabel, getTransportStopPluralizeLabel, PLACES_POI_CONFIG} from "../utils/poi_type_utils";
import {RegionTransportRoutes} from "./RegionTransportRoutes";

interface IProps {
    country: Country;
    nearbyTransportPois?: Record<TransportPoiType, IPoi[]> | null;
    isTransportPoiLoading?: boolean;
    isPoisStatsLoading?: boolean;
    isRoutesLoading?: boolean;
    nearestPoisCount: Record<PoiType, NearestPoiTypeStats> | undefined;
    routes?: {
        busRoutes: IPublicTransportRoute[] | null;
        railwayRoutes: IPublicTransportRoute[] | null;
        subwayRoutes: IPublicTransportRoute[] | null;
        tramRoutes: IPublicTransportRoute[] | null;
    } | null;
    activePoiRoute: IPublicTransportRoute | null;
    onTransportRouteClick: (route: IPublicTransportRoute) => void;
    onTransportRoutesExpand?: () => void;
    dataTestId?: string;
    className?: string;
}

export function LocationPoisSummary(props: IProps) {
    const {
        country,
        nearbyTransportPois,
        isTransportPoiLoading,
        isPoisStatsLoading,
        isRoutesLoading,
        nearestPoisCount,
        routes,
        activePoiRoute,
        onTransportRouteClick,
        onTransportRoutesExpand,
        dataTestId
    } = props;

    if (!getHasCountryPois(country)) {
        return null;
    }

    if (isPoisStatsLoading || isTransportPoiLoading) {
        return (
            <div className={loaderWrapperStyle}>
                <Loader />
            </div>
        );
    }

    const tramPois = nearbyTransportPois?.[TransportPoiType.TRAM] ?? [];
    const busPois = nearbyTransportPois?.[TransportPoiType.BUS] ?? [];
    const subwayPois = nearbyTransportPois?.[TransportPoiType.SUBWAY] ?? [];
    const railwayPois = nearbyTransportPois?.[TransportPoiType.RAILWAY] ?? [];

    const nearbyPlaces = PLACES_POI_CONFIG.map((row) => ({...row, count: nearestPoisCount?.[row.type]?.count ?? 0})).filter((row) => row.count > 0);

    const hasTransportSection = tramPois.length > 0 || busPois.length > 0 || subwayPois.length > 0 || railwayPois.length > 0;
    const hasNearbyAreaSection = nearbyPlaces.length > 0;
    const hasAnyRoutes = Boolean(routes?.tramRoutes?.length || routes?.busRoutes?.length || routes?.subwayRoutes?.length || routes?.railwayRoutes?.length);

    if (!hasTransportSection && !hasNearbyAreaSection) {
        return null;
    }

    return (
        <div data-testid={dataTestId} className={cx(sectionWrapperStyle, props.className)}>
            <Text as="p" variant="headline_6" mb={3}>
                <strong>W pobliżu {POI_DISTANCE_DEFAULT_VALUE} km</strong> od osiedla znajdziesz:
            </Text>

            <div className={columnsWrapperStyle}>
                {hasTransportSection && (
                    <div className={columnStyle}>
                        <div className={communicationHeading}>
                            <Text as="div" variant="mini_header" mb={0.5}>
                                Komunikacja
                            </Text>

                            {hasAnyRoutes && (
                                <Text as="div" variant="body_copy_2" color="var(--colors-gray-600)">
                                    Kliknij na numer linii, aby zobaczyć połączenie
                                </Text>
                            )}
                        </div>
                        {tramPois.length > 0 && (
                            <div className={transportGroupStyle}>
                                <div className={transportGroupHeadingStyle}>
                                    <PoiTramIcon size="1.6" wrapperSize="2" wrapperColor="transparent" />
                                    <Text as="span" variant="body_copy_1">
                                        {getTransportStopPluralizeLabel(TransportPoiType.TRAM, tramPois.length)} ({getNearestStopLabel(TransportPoiType.TRAM)}{" "}
                                        {getNearestPoiDistance(tramPois)} m)
                                    </Text>
                                </div>
                                {isRoutesLoading ? (
                                    <div className={groupRoutesLoaderStyle}>
                                        <Loader />
                                    </div>
                                ) : routes?.tramRoutes && routes.tramRoutes.length > 0 ? (
                                    <RegionTransportRoutes
                                        tramRoutes={routes.tramRoutes}
                                        activePoiRoute={activePoiRoute}
                                        onTransportRouteClick={onTransportRouteClick}
                                        onExpand={onTransportRoutesExpand}
                                    />
                                ) : (
                                    <TransportRoutesLoadError />
                                )}
                            </div>
                        )}

                        {busPois.length > 0 && (
                            <div className={transportGroupStyle}>
                                <div className={transportGroupHeadingStyle}>
                                    <PoiBusIcon size="1.6" wrapperSize="2" wrapperColor="transparent" />
                                    <Text as="span" variant="body_copy_1">
                                        {getTransportStopPluralizeLabel(TransportPoiType.BUS, busPois.length)} ({getNearestStopLabel(TransportPoiType.BUS)}{" "}
                                        {getNearestPoiDistance(busPois)} m)
                                    </Text>
                                </div>
                                {isRoutesLoading ? (
                                    <div className={groupRoutesLoaderStyle}>
                                        <Loader />
                                    </div>
                                ) : routes?.busRoutes && routes.busRoutes.length > 0 ? (
                                    <RegionTransportRoutes
                                        busRoutes={routes.busRoutes}
                                        activePoiRoute={activePoiRoute}
                                        onTransportRouteClick={onTransportRouteClick}
                                        onExpand={onTransportRoutesExpand}
                                    />
                                ) : (
                                    <TransportRoutesLoadError />
                                )}
                            </div>
                        )}

                        {subwayPois.length > 0 && (
                            <div className={transportGroupStyle}>
                                <div className={transportGroupHeadingStyle}>
                                    <PoiSubwayIcon size="1.6" wrapperSize="2" wrapperColor="transparent" />
                                    <Text as="span" variant="body_copy_1">
                                        {getTransportStopPluralizeLabel(TransportPoiType.SUBWAY, subwayPois.length)} (
                                        {getNearestStopLabel(TransportPoiType.SUBWAY)} {getNearestPoiDistance(subwayPois)} m)
                                    </Text>
                                </div>
                                {isRoutesLoading ? (
                                    <div className={groupRoutesLoaderStyle}>
                                        <Loader />
                                    </div>
                                ) : routes?.subwayRoutes && routes.subwayRoutes.length > 0 ? (
                                    <RegionTransportRoutes
                                        subwayRoutes={routes.subwayRoutes}
                                        activePoiRoute={activePoiRoute}
                                        onTransportRouteClick={onTransportRouteClick}
                                        onExpand={onTransportRoutesExpand}
                                    />
                                ) : (
                                    <TransportRoutesLoadError />
                                )}
                            </div>
                        )}

                        {railwayPois.length > 0 && (
                            <div className={transportGroupStyle}>
                                <div className={transportGroupHeadingStyle}>
                                    <PoiTrainIcon size="1.6" wrapperSize="2" wrapperColor="transparent" />
                                    <Text as="span" variant="body_copy_1">
                                        {getTransportStopPluralizeLabel(TransportPoiType.RAILWAY, railwayPois.length)} (
                                        {getNearestStopLabel(TransportPoiType.RAILWAY)} {getNearestPoiDistance(railwayPois)} m)
                                    </Text>
                                </div>
                                {isRoutesLoading ? (
                                    <div className={groupRoutesLoaderStyle}>
                                        <Loader />
                                    </div>
                                ) : routes?.railwayRoutes && routes.railwayRoutes.length > 0 ? (
                                    <RegionTransportRoutes
                                        railwayRoutes={routes.railwayRoutes}
                                        activePoiRoute={activePoiRoute}
                                        onTransportRouteClick={onTransportRouteClick}
                                        onExpand={onTransportRoutesExpand}
                                    />
                                ) : (
                                    <TransportRoutesLoadError />
                                )}
                            </div>
                        )}
                    </div>
                )}

                {hasNearbyAreaSection && (
                    <div className={nearbyAreaCardStyle}>
                        <Text as="div" variant="mini_header" mb={1}>
                            Okolica
                        </Text>

                        {nearbyPlaces.map(({type, icon: Icon, color, count}) => (
                            <div key={type} className={nearbyAreaRowStyle}>
                                <Icon size="1.6" wrapperSize="2" wrapperColor={color} wrapperType="circle" />
                                <Text as="span" variant="body_copy_1">
                                    {getPoiPluralizeLabel(type, count)}
                                </Text>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function TransportRoutesLoadError() {
    return (
        <Text as="p" variant="body_copy_2" color="var(--colors-gray-600)">
            Nie udało się załadować linii komunikacji
        </Text>
    );
}

const sectionWrapperStyle = css({
    px: 1.5,
    py: 2,

    md: {
        px: 3,
        py: 3
    }
});

const loaderWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    py: 5
});

const columnsWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 3,

    md: {
        flexDirection: "row",
        gap: 4
    }
});

const columnStyle = css({
    flex: 1,
    minWidth: 0
});

const communicationHeading = css({
    display: "none",

    md: {
        display: "block",
        mb: 3
    }
});

const transportGroupStyle = css({
    mb: 3,
    pb: 2,
    borderBottom: "1px solid var(--colors-gray-200)",

    "&:last-child": {
        mb: 0,
        pb: 0,
        borderBottom: "none"
    }
});

const transportGroupHeadingStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1
});

const groupRoutesLoaderStyle = css({
    display: "flex",
    mt: 0.5
});

const nearbyAreaCardStyle = css({
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
    p: 2,
    gap: 1,
    borderRadius: 2,
    background: "var(--colors-gray-100)",
    alignSelf: "center",

    md: {
        alignSelf: "flex-start"
    }
});

const nearbyAreaRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1
});
import {css, cx} from "@panda/css";

import {PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";

import {NearestPoiTypeStats} from "../../offer/detail/actions/fetch_offer_detail_poi";
import {Country} from "../../region/types/Country";
import {POI_DISTANCE_DEFAULT_VALUE} from "../constants/pois_defaults";
import {IPoi} from "../types/IPoi";
import {IPublicTransportRoute} from "../types/IPublicTransportRoute";
import {PoiType, TransportPoiType} from "../types/PoiType";
import {getHasCountryPois} from "../utils/get_has_country_pois";
import {getNearestPoiDistance} from "../utils/get_nearest_poi_distance";
import {getNearestStopLabel, getPoiPluralizeLabel, getTransportStopPluralizeLabel, PLACES_POI_CONFIG} from "../utils/poi_type_utils";
import {RegionTransportRoutes} from "./RegionTransportRoutes";

interface IProps {
    country: Country;
    nearbyTransportPois?: Record<TransportPoiType, IPoi[]> | null;
    isTransportPoiLoading?: boolean;
    isPoisStatsLoading?: boolean;
    isRoutesLoading?: boolean;
    nearestPoisCount: Record<PoiType, NearestPoiTypeStats> | undefined;
    routes?: {
        busRoutes: IPublicTransportRoute[] | null;
        railwayRoutes: IPublicTransportRoute[] | null;
        subwayRoutes: IPublicTransportRoute[] | null;
        tramRoutes: IPublicTransportRoute[] | null;
    } | null;
    activePoiRoute: IPublicTransportRoute | null;
    onTransportRouteClick: (route: IPublicTransportRoute) => void;
    onTransportRoutesExpand?: () => void;
    dataTestId?: string;
    className?: string;
}

export function LocationPoisSummary(props: IProps) {
    const {
        country,
        nearbyTransportPois,
        isTransportPoiLoading,
        isPoisStatsLoading,
        isRoutesLoading,
        nearestPoisCount,
        routes,
        activePoiRoute,
        onTransportRouteClick,
        onTransportRoutesExpand,
        dataTestId
    } = props;

    if (!getHasCountryPois(country)) {
        return null;
    }

    if (isPoisStatsLoading || isTransportPoiLoading) {
        return (
            <div className={loaderWrapperStyle}>
                <Loader />
            </div>
        );
    }

    const tramPois = nearbyTransportPois?.[TransportPoiType.TRAM] ?? [];
    const busPois = nearbyTransportPois?.[TransportPoiType.BUS] ?? [];
    const subwayPois = nearbyTransportPois?.[TransportPoiType.SUBWAY] ?? [];
    const railwayPois = nearbyTransportPois?.[TransportPoiType.RAILWAY] ?? [];

    const nearbyPlaces = PLACES_POI_CONFIG.map((row) => ({...row, count: nearestPoisCount?.[row.type]?.count ?? 0})).filter((row) => row.count > 0);

    const hasTransportSection = tramPois.length > 0 || busPois.length > 0 || subwayPois.length > 0 || railwayPois.length > 0;
    const hasNearbyAreaSection = nearbyPlaces.length > 0;
    const hasAnyRoutes = Boolean(routes?.tramRoutes?.length || routes?.busRoutes?.length || routes?.subwayRoutes?.length || routes?.railwayRoutes?.length);

    if (!hasTransportSection && !hasNearbyAreaSection) {
        return null;
    }

    return (
        <div data-testid={dataTestId} className={cx(sectionWrapperStyle, props.className)}>
            <Text as="p" variant="headline_6" mb={3}>
                <strong>W pobliżu {POI_DISTANCE_DEFAULT_VALUE} km</strong> od osiedla znajdziesz:
            </Text>

            <div className={columnsWrapperStyle}>
                {hasTransportSection && (
                    <div className={columnStyle}>
                        <div className={communicationHeading}>
                            <Text as="div" variant="mini_header" mb={0.5}>
                                Komunikacja
                            </Text>

                            {hasAnyRoutes && (
                                <Text as="div" variant="body_copy_2" color="var(--colors-gray-600)">
                                    Kliknij na numer linii, aby zobaczyć połączenie
                                </Text>
                            )}
                        </div>
                        {tramPois.length > 0 && (
                            <div className={transportGroupStyle}>
                                <div className={transportGroupHeadingStyle}>
                                    <PoiTramIcon size="1.6" wrapperSize="2" wrapperColor="transparent" />
                                    <Text as="span" variant="body_copy_1">
                                        {getTransportStopPluralizeLabel(TransportPoiType.TRAM, tramPois.length)} ({getNearestStopLabel(TransportPoiType.TRAM)}{" "}
                                        {getNearestPoiDistance(tramPois)} m)
                                    </Text>
                                </div>
                                {isRoutesLoading ? (
                                    <div className={groupRoutesLoaderStyle}>
                                        <Loader />
                                    </div>
                                ) : routes?.tramRoutes && routes.tramRoutes.length > 0 ? (
                                    <RegionTransportRoutes
                                        tramRoutes={routes.tramRoutes}
                                        activePoiRoute={activePoiRoute}
                                        onTransportRouteClick={onTransportRouteClick}
                                        onExpand={onTransportRoutesExpand}
                                    />
                                ) : (
                                    <TransportRoutesLoadError />
                                )}
                            </div>
                        )}

                        {busPois.length > 0 && (
                            <div className={transportGroupStyle}>
                                <div className={transportGroupHeadingStyle}>
                                    <PoiBusIcon size="1.6" wrapperSize="2" wrapperColor="transparent" />
                                    <Text as="span" variant="body_copy_1">
                                        {getTransportStopPluralizeLabel(TransportPoiType.BUS, busPois.length)} ({getNearestStopLabel(TransportPoiType.BUS)}{" "}
                                        {getNearestPoiDistance(busPois)} m)
                                    </Text>
                                </div>
                                {isRoutesLoading ? (
                                    <div className={groupRoutesLoaderStyle}>
                                        <Loader />
                                    </div>
                                ) : routes?.busRoutes && routes.busRoutes.length > 0 ? (
                                    <RegionTransportRoutes
                                        busRoutes={routes.busRoutes}
                                        activePoiRoute={activePoiRoute}
                                        onTransportRouteClick={onTransportRouteClick}
                                        onExpand={onTransportRoutesExpand}
                                    />
                                ) : (
                                    <TransportRoutesLoadError />
                                )}
                            </div>
                        )}

                        {subwayPois.length > 0 && (
                            <div className={transportGroupStyle}>
                                <div className={transportGroupHeadingStyle}>
                                    <PoiSubwayIcon size="1.6" wrapperSize="2" wrapperColor="transparent" />
                                    <Text as="span" variant="body_copy_1">
                                        {getTransportStopPluralizeLabel(TransportPoiType.SUBWAY, subwayPois.length)} (
                                        {getNearestStopLabel(TransportPoiType.SUBWAY)} {getNearestPoiDistance(subwayPois)} m)
                                    </Text>
                                </div>
                                {isRoutesLoading ? (
                                    <div className={groupRoutesLoaderStyle}>
                                        <Loader />
                                    </div>
                                ) : routes?.subwayRoutes && routes.subwayRoutes.length > 0 ? (
                                    <RegionTransportRoutes
                                        subwayRoutes={routes.subwayRoutes}
                                        activePoiRoute={activePoiRoute}
                                        onTransportRouteClick={onTransportRouteClick}
                                        onExpand={onTransportRoutesExpand}
                                    />
                                ) : (
                                    <TransportRoutesLoadError />
                                )}
                            </div>
                        )}

                        {railwayPois.length > 0 && (
                            <div className={transportGroupStyle}>
                                <div className={transportGroupHeadingStyle}>
                                    <PoiTrainIcon size="1.6" wrapperSize="2" wrapperColor="transparent" />
                                    <Text as="span" variant="body_copy_1">
                                        {getTransportStopPluralizeLabel(TransportPoiType.RAILWAY, railwayPois.length)} (
                                        {getNearestStopLabel(TransportPoiType.RAILWAY)} {getNearestPoiDistance(railwayPois)} m)
                                    </Text>
                                </div>
                                {isRoutesLoading ? (
                                    <div className={groupRoutesLoaderStyle}>
                                        <Loader />
                                    </div>
                                ) : routes?.railwayRoutes && routes.railwayRoutes.length > 0 ? (
                                    <RegionTransportRoutes
                                        railwayRoutes={routes.railwayRoutes}
                                        activePoiRoute={activePoiRoute}
                                        onTransportRouteClick={onTransportRouteClick}
                                        onExpand={onTransportRoutesExpand}
                                    />
                                ) : (
                                    <TransportRoutesLoadError />
                                )}
                            </div>
                        )}
                    </div>
                )}

                {hasNearbyAreaSection && (
                    <div className={nearbyAreaCardStyle}>
                        <Text as="div" variant="mini_header" mb={1}>
                            Okolica
                        </Text>

                        {nearbyPlaces.map(({type, icon: Icon, color, count}) => (
                            <div key={type} className={nearbyAreaRowStyle}>
                                <Icon size="1.6" wrapperSize="2" wrapperColor={color} wrapperType="circle" />
                                <Text as="span" variant="body_copy_1">
                                    {getPoiPluralizeLabel(type, count)}
                                </Text>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function TransportRoutesLoadError() {
    return (
        <Text as="p" variant="body_copy_2" color="var(--colors-gray-600)">
            Nie udało się załadować linii komunikacji
        </Text>
    );
}

const sectionWrapperStyle = css({
    px: 1.5,
    py: 2,

    md: {
        px: 3,
        py: 3
    }
});

const loaderWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    py: 5
});

const columnsWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 3,

    md: {
        flexDirection: "row",
        gap: 4
    }
});

const columnStyle = css({
    flex: 1,
    minWidth: 0
});

const communicationHeading = css({
    display: "none",

    md: {
        display: "block",
        mb: 3
    }
});

const transportGroupStyle = css({
    mb: 3,
    pb: 2,
    borderBottom: "1px solid var(--colors-gray-200)",

    "&:last-child": {
        mb: 0,
        pb: 0,
        borderBottom: "none"
    }
});

const transportGroupHeadingStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1
});

const groupRoutesLoaderStyle = css({
    display: "flex",
    mt: 0.5
});

const nearbyAreaCardStyle = css({
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
    p: 2,
    gap: 1,
    borderRadius: 2,
    background: "var(--colors-gray-100)",
    alignSelf: "center",

    md: {
        alignSelf: "flex-start"
    }
});

const nearbyAreaRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1
});
